import  { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AddAddressModal, { AddressData } from "../modal/AddAddressModal";
import LocationPickerModal from "../modal/LocationPickerModal";
import { addressBookService, AddressBookData } from "../services/index";
import { useCart } from "../context/CartContext";
import { GOOGLE_MAPS_API_KEY } from "../utils/constants";
import {
  persistAddressDeliveryInstructions,
  joinDeliveryInstructions,
} from "../utils/helpers";

const SavedAddresses = () => {
  const navigate = useNavigate();
  const { cartItems } = useCart();
  const [addresses, setAddresses] = useState<AddressBookData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddAddressModalOpen, setIsAddAddressModalOpen] = useState(false);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
  const [selectedLocationData, setSelectedLocationData] = useState<any>(null);
  const [editingAddress, setEditingAddress] = useState<AddressBookData | null>(null);
  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] = useState<string | null>(null);
  const locationConfirmedRef = useRef(false);
  
  // Fetch all addresses
  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const response: any = await addressBookService.getAllAddresses();
      if (response.status && response.data) {
        setAddresses(response.data);
        
        // Load selected delivery address from localStorage
        const savedAddressId = localStorage.getItem('selectedDeliveryAddressId');
        if (savedAddressId) {
          setSelectedDeliveryAddressId(savedAddressId);
        } else {
          // If no saved selection, use default address
          const defaultAddress = response.data.find((addr: AddressBookData) => addr.isDefault);
          if (defaultAddress && defaultAddress._id) {
            setSelectedDeliveryAddressId(defaultAddress._id);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  // Handle location selection from map
  const handleLocationSelect = (locationData: any) => {
    console.log("Location selected from map:", locationData);
    
    // Format the location data properly for AddAddressModal
    const formattedLocationData = {
      address: locationData.address,
      pincode: locationData.pincode,
      city: locationData.city,
      state: locationData.state,
      coordinates: locationData.coordinates,
      fullAddress: locationData.fullAddress, // Ensure fullAddress is passed
    };
    
    console.log("Formatted location data:", formattedLocationData);
    
    // Mark that location was confirmed (not cancelled)
    locationConfirmedRef.current = true;
    
    setSelectedLocationData(formattedLocationData);
    setIsLocationPickerOpen(false);
    
    // Open the form modal with pre-filled location data
    setIsAddAddressModalOpen(true);
  };

  // Handle saving new address
  const handleSaveAddress = async (addressData: AddressData) => {
    try {
      if (editingAddress && editingAddress._id) {
        // Update existing address
        const updateData = {
          fullName: addressData.fullName,
          phone: addressData.phone,
          addressLine1: addressData.addressLine1,
          addressLine2: addressData.addressLine2,
          city: addressData.city,
          state: addressData.state,
          pincode: addressData.pincode,
          geoLocation: addressData.geoLocation,
          deliveryInstructions: joinDeliveryInstructions(
            addressData.deliveryInstructions
          ),
        };
        const response: any = await addressBookService.updateAddress(
          editingAddress._id,
          updateData
        );
        if (response.status) {
          persistAddressDeliveryInstructions(
            editingAddress._id,
            addressData.deliveryInstructions
          );
          await fetchAddresses();
          setIsAddAddressModalOpen(false);
          setEditingAddress(null);
          setSelectedLocationData(null);
        }
      } else {
        // Create new address
        const createData = {
          fullName: addressData.fullName,
          phone: addressData.phone,
          addressLine1: addressData.addressLine1,
          addressLine2: addressData.addressLine2,
          city: addressData.city,
          state: addressData.state,
          pincode: addressData.pincode,
          geoLocation: addressData.geoLocation,
          deliveryInstructions: joinDeliveryInstructions(
            addressData.deliveryInstructions
          ),
        };
        const response: any = await addressBookService.createAddress(createData);
        if (response.status) {
          persistAddressDeliveryInstructions(
            response.data?._id,
            addressData.deliveryInstructions
          );
          await fetchAddresses();
          setIsAddAddressModalOpen(false);
          setSelectedLocationData(null);
        }
      }
    } catch (error) {
      console.error("Error saving address:", error);
    }
  };

  // Handle set default address
  const handleSetDefault = async (addressId: string | undefined) => {
    if (!addressId) return;
    try {
      const response: any = await addressBookService.setDefaultAddress(addressId);
      if (response.status) {
        await fetchAddresses();
      }
    } catch (error) {
      console.error("Error setting default address:", error);
    }
  };

  // Handle delete address
  const handleDelete = async (addressId: string | undefined) => {
    if (!addressId) return;
    if (window.confirm("Are you sure you want to delete this address?")) {
      try {
        const response: any = await addressBookService.deleteAddress(addressId);
        if (response.status) {
          await fetchAddresses();
        }
      } catch (error) {
        console.error("Error deleting address:", error);
      }
    }
  };

  // Handle edit address
  const handleEdit = (address: AddressBookData) => {
    console.log("Editing address:", address);
    // Store the address being edited
    setEditingAddress(address);
    // Open location picker to allow updating the location
    setIsLocationPickerOpen(true);
  };
  console.log("editingAddress", editingAddress)
  // Handle deliver here button
  const handleDeliverHere = (addressId: string | undefined) => {
    if (!addressId) return;
    // Save selected address to localStorage
    localStorage.setItem('selectedDeliveryAddressId', addressId);
    setSelectedDeliveryAddressId(addressId);
    // Navigate back to order summary
    navigate('/order-summary');
  };

  return (
    <div className="min-h-screen bg-[#FEFAF4] dark:bg-gray-900 transition-colors duration-300 md:pt-0 pt-0">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-4 md:px-8 py-4 md:py-6 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <svg
                className="w-6 h-6 text-gray-800 dark:text-white rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
            <h1 className="text-[24px] md:text-[32px] font-semibold text-gray-900 dark:text-white">
              Saved Addresses
            </h1>
          </div>
          <button 
            onClick={() => setIsLocationPickerOpen(true)}
            className="hidden md:flex items-center gap-2 px-6 py-3 bg-[#7402A7] dark:bg-purple-600 text-white rounded-full font-semibold hover:bg-[#5f0289] dark:hover:bg-purple-700 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Address
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-4 md:p-8 pb-24 md:pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-lg text-gray-600 dark:text-gray-400">
              Loading addresses...
            </div>
          </div>
        ) : addresses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-lg text-gray-600 dark:text-gray-400 mb-4">
              No saved addresses
            </div>
            <button
              onClick={() => setIsLocationPickerOpen(true)}
              className="px-6 py-3 bg-[#7402A7] dark:bg-purple-600 text-white rounded-full font-semibold hover:bg-[#5f0289] dark:hover:bg-purple-700 transition-colors"
            >
              + Add New Address
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {addresses.map((address) => (
              <div
                key={address._id}
                className={`bg-white dark:bg-gray-800 rounded-[20px] p-5 ${
                  address.isDefault
                    ? "border-2 border-[#7402A7] dark:border-purple-600"
                    : "border border-gray-200 dark:border-gray-700"
                } transition-colors duration-300 hover:shadow-lg`}
              >
                {/* Default Badge */}
                {address.isDefault && (
                  <div className="mb-3">
                    <span className="inline-block px-4 py-1 bg-[#7402A7] dark:bg-purple-600 text-white text-sm font-medium rounded-full">
                      Default
                    </span>
                  </div>
                )}

                {/* Address Details */}
                <div className="mb-4">
                  <h3 className="text-[18px] font-semibold text-gray-900 dark:text-white mb-2">
                    {address.fullName}
                  </h3>
                  <p className="text-[15px] text-gray-700 dark:text-gray-300 leading-relaxed">
                    {address.addressLine1}
                    {address.addressLine2 && `, ${address.addressLine2}`}
                  </p>
                  <p className="text-[15px] text-gray-600 dark:text-gray-400">
                    {address.city}, {address.state} - {address.pincode}
                  </p>
                  <p className="text-[14px] text-gray-600 dark:text-gray-400 mt-2">
                    +{address.phone}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  {/* Show Selected or Deliver Here button */}
                  {address._id === selectedDeliveryAddressId ? (
                    <button
                      className="w-full px-4 py-2.5 bg-[#7402A7] dark:bg-purple-600 text-white rounded-[10px] font-medium cursor-default"
                      disabled
                    >
                      Selected
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDeliverHere(address._id)}
                      className="w-full px-4 py-2.5 border-2 border-[#7402A7] dark:border-purple-600 text-[#7402A7] dark:text-purple-400 rounded-[10px] font-medium hover:bg-purple-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Deliver Here
                    </button>
                  )}
                  
                  {/* Set Default button (only if not default) */}
                  {!address.isDefault && (
                    <button
                      onClick={() => handleSetDefault(address._id)}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-[10px] font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      Set Default
                    </button>
                  )}
                  
                  {/* Edit and Delete buttons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(address)}
                      className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-[10px] font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(address._id)}
                      className="flex-1 px-4 py-2.5 border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 rounded-[10px] font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add New Address Button - Fixed at bottom (Mobile only) - Hidden when cart has items to avoid overlap */}
      {cartItems.length === 0 && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-gray-800 border-t dark:border-gray-700">
          <button
            onClick={() => setIsLocationPickerOpen(true)}
            className="w-full py-4 bg-[#7402A7] dark:bg-purple-600 text-white text-[18px] font-semibold rounded-full hover:bg-[#5f0289] dark:hover:bg-purple-700 transition-colors"
          >
            + Add New Address
          </button>
        </div>
      )}

      {/* Location Picker Modal */}
      {isLocationPickerOpen && (
        <LocationPickerModal
          isOpen={isLocationPickerOpen}
          onClose={() => {
            setIsLocationPickerOpen(false);
            // Only clear if user cancelled (didn't confirm location)
            // If location was confirmed, data will be cleared when AddAddressModal closes
            if (!locationConfirmedRef.current) {
              setSelectedLocationData(null);
              setEditingAddress(null);
            }
            // Reset the flag for next time
            locationConfirmedRef.current = false;
          }}
          onLocationSelect={handleLocationSelect}
          initialLocation={
            editingAddress?.geoLocation
              ? {
                  lat: editingAddress.geoLocation.coordinates[1],
                  lng: editingAddress.geoLocation.coordinates[0],
                }
              : undefined
          }
          googleMapsApiKey={GOOGLE_MAPS_API_KEY}
        />
      )}

      {/* Add/Edit Address Modal */}
      {isAddAddressModalOpen && (
        <AddAddressModal
          isOpen={isAddAddressModalOpen}
          onClose={() => {
            setIsAddAddressModalOpen(false);
            setEditingAddress(null);
            setSelectedLocationData(null);
          }}
          onSave={handleSaveAddress}
          initialLocationData={selectedLocationData}
          existingAddress={editingAddress}
        />
      )}
    </div>
  );
};

export default SavedAddresses;
